import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Modal } from 'reactstrap'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import ButtonCommon from '../../../components/Button'
import './style.scss'
// eslint-disable-next-line import/order
import { editProductAction } from '_redux/modules/product'

const editProductSchema = Yup.object().shape({
  name: Yup.string().required('Product name is required'),
  description: Yup.string().nullable().notRequired(),
  price: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? NaN : value))
    .typeError('In valid')
    .min(0, 'Price must be greater than or equal to 0')
    .required('Is required'),
  minOrderQty: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? NaN : value))
    .typeError('In valid')
    .min(1, 'Minimum order quantity must be at least 1')
    .required('Is required'),
})

export default function ModalEditProduct({
  isOpen,
  toggle,
  row = null,
}) {
  const dispatch = useDispatch()
  const { addToast } = useToasts()

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(editProductSchema),
    defaultValues: {
      name: '',
      description: '',
      price: '',
      minOrderQty: 1,
    },
  })

  useEffect(() => {
    if (isOpen && row) {
      reset({
        name: row.name ?? '',
        description: row.description ?? '',
        price: row.price ?? '',
        minOrderQty: row.minOrderQty ?? 1,
      })
    }
  }, [row, isOpen, reset])

  const onSubmit = async (data) => {
    if (!row?.id) return

    const payload = {
      name: data.name?.trim(),
      description: data.description?.trim() || '',
      price: Number(data.price),
      minOrderQty: Number(data.minOrderQty),
    }

    try {
      const { msgResp } = await dispatch(
        editProductAction(row.id, row.eventId, payload)
      )
      addToast(msgResp || 'Update product successfully', {
        appearance: 'success',
        autoDismiss: true,
      })
      toggle()
    } catch (error) {
      addToast(error?.msgResp || 'Update product failed', { appearance: 'error', autoDismiss: true })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="prod-modal-dialog"
    >
      <div className="prod-modal-hd">
        <h3 className="prod-modal-title">Edit Product</h3>
        <button
          type="button"
          className="prod-modal-close"
          onClick={toggle}
        >
          ×
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="prod-modal-body">
          <div className="prod-modal-form-group">
            <label className="prod-modal-label">
              Product Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`prod-modal-input ${errors.name ? 'is-invalid' : ''}`}
              {...register('name')}
              placeholder="Enter product name"
            />
            {errors.name && (
              <span className="prod-modal-error">{errors.name.message}</span>
            )}
          </div>

          <div className="prod-modal-form-group">
            <label className="prod-modal-label">Description</label>
            <textarea
              className={`prod-modal-textarea ${errors.description ? 'is-invalid' : ''}`}
              {...register('description')}
              placeholder="Enter description"
              rows={3}
            />
            {errors.description && (
              <span className="prod-modal-error">{errors.description.message}</span>
            )}
          </div>

          <div className="prod-modal-row">
            <div className="prod-modal-form-group">
              <label className="prod-modal-label">
                Price ($) <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                step="any"
                className={`prod-modal-input ${errors.price ? 'is-invalid' : ''}`}
                {...register('price')}
              />
              {errors.price && (
                <span className="prod-modal-error">{errors.price.message}</span>
              )}
            </div>

            <div className="prod-modal-form-group">
              <label className="prod-modal-label">
                Min Order Qty <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="1"
                className={`prod-modal-input ${errors.minOrderQty ? 'is-invalid' : ''}`}
                {...register('minOrderQty')}
              />
              {errors.minOrderQty && (
                <span className="prod-modal-error">{errors.minOrderQty.message}</span>
              )}
            </div>
          </div>
        </div>

        <div className="prod-modal-footer">
          <ButtonCommon
            variant="outline"
            content="Cancel"
            onClick={toggle}
          />
          <ButtonCommon
            variant="primary"
            htmlType="submit"
            content="Save"
            disabled={isSubmitting}
          />
        </div>
      </form>
    </Modal>
  )
}
